import React, { createContext, useContext } from 'react';
import { documentEditorWrapperSx } from './documentEditorLayout';
import { WorkflowAssistantHeaderContext } from './WorkflowAssistantHeader';
import {
  Box,
  Paper,
  Typography,
  Button,
  Container,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { alpha, SxProps, Theme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import { StageConfig } from '../types';
import MultiItemWarningBanner from './MultiItemWarningBanner';
import { Transition } from './motion';
import StageRatingWidget from './StageRatingWidget';
import { RatingValue, stageRatingId } from '../api/stageRatingFunctions';
import { WorkflowType } from './WorkflowTypeGrid';
import { MOBILE_DENSITY, phoneMedia } from '../theme/mobileDensity';
import {
  MarketResearchEditAwarenessProvider,
  MarketResearchEditIndicatorSlot,
  type MarketResearchInlineEdit,
} from './MarketResearchEditIndicators';

interface FlowContextValue {
  flowName: string;
  /** Workflow family lets shared stages opt into family-specific adjunct UI. */
  workflowType?: WorkflowType;
  onBack?: () => void;
  canGoBack: boolean;
  /**
   * Count of distinct product types Stage 1 detected in the customer's
   * requirement text (propagated from
   * `stage{N}-ui.json::output.num_items_detected`). Greater than 1 means
   * multiple distinct products; defaults to 1 (single product).
   * Available to any descendant via `useFlow()`.
   */
  numItemsDetected?: number;
  standaloneMode: boolean;
  /**
   * View-only ("read-only example") mode. True when the open flow is a curated
   * training/example workflow (backend `metadata.read_only === true`). Stages
   * read this via `useFlow()` to suppress auto-fire writes/generation and to
   * disable action controls. Defaults to false, exactly parallel to
   * `standaloneMode`, so non-training flows are unaffected.
   */
  isViewOnly: boolean;
  readOnlyReason?: 'similar_requirements' | 'super_admin_view' | 'training' | 'role_preview' | null;
  /** True only for a curated sample, not for a role preview of a normal flow. */
  isSampleFlow?: boolean;
  /** Move to the next applicable sample stage without saving stage data. */
  onSampleContinue?: () => void;
}

export const FlowContext = createContext<FlowContextValue>({
  flowName: '',
  workflowType: undefined,
  canGoBack: false,
  numItemsDetected: 1,
  standaloneMode: false,
  isViewOnly: false,
  isSampleFlow: false,
});

export const useFlow = (): FlowContextValue => useContext(FlowContext);

export const StageBackButton: React.FC<{ label?: string; sx?: SxProps<Theme>; onClick?: () => void; disabled?: boolean }> = ({ label = 'Back', sx, onClick, disabled }) => {
  const { onBack, canGoBack, standaloneMode } = useFlow();
  if (!onClick && (!canGoBack || !onBack || standaloneMode)) return null;
  return (
    <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onClick ?? onBack} disabled={disabled} sx={sx}>
      {label}
    </Button>
  );
};

export const appendWorkflowName = (baseTitle: string, workflowName?: string | null): string => {
  const trimmed = (workflowName ?? '').trim();
  return trimmed ? `${baseTitle}: ${trimmed}` : baseTitle;
};

const STAGE_NAV_END_CLEARANCE = 6;

const stageLabelLines = (label: string): string[] => {
  const words = label.trim().split(/\s+/);
  if (words.length < 2 || label.length <= 13) return [label];

  let bestBreak = 1;
  let bestWidth = Infinity;
  for (let index = 1; index < words.length; index += 1) {
    const first = words.slice(0, index).join(' ');
    const second = words.slice(index).join(' ');
    const width = Math.max(first.length, second.length);
    if (width < bestWidth) {
      bestWidth = width;
      bestBreak = index;
    }
  }
  return [words.slice(0, bestBreak).join(' '), words.slice(bestBreak).join(' ')];
};

interface StageWrapperProps {
  config: StageConfig;
  currentStage: number;
  totalStages: number;
  stages: string[];
  workflowName?: string | null;
  workflowId?: string | null;
  /** Display name for the workflow type (e.g. "Market Research"). */
  workflowTypeName?: string;
  /** Optional stage help text shown in the subtitle slot. */
  stageHelp?: string;
  onBack: () => void;
  // Save action removed; legacy
  onStageSelect?: (stageIndex: number) => void;
  children: React.ReactNode;
  /** Opt in to the viewport's available width. Defaults to the shared xl cap. */
  fullWidth?: boolean;
  /** Keep navigation on one row, scrolling when its stages do not fit. */
  compactNavigation?: boolean;
  isProcessing?: boolean;
  processingMessage?: string;
  standaloneMode?: boolean;
  standaloneStartIndex?: number;
  /**
   * View-only ("read-only example") mode for curated training flows. Provided
   * through `FlowContext` (mirrors `standaloneMode`) and surfaces a
   * "Read-only example" banner in the header. Defaults to false.
   */
  isViewOnly?: boolean;
  readOnlyReason?: 'similar_requirements' | 'super_admin_view' | 'training' | 'role_preview' | null;
  isSampleFlow?: boolean;
  onSampleContinue?: () => void;
  /**
   * Count of distinct product types Stage 1 detected. The multi-item
   * warning banner above the stage content renders only when this value
   * is greater than 1. Sourced from Stage 1's `num_items_detected` output
   * and persists through every downstream stage.
   */
  numItemsDetected?: number;
  /**
   * Stepper indices that are DISABLED/inapplicable for the active flow. They
   * render greyed and are non-clickable (no `onStageSelect` fires), but they are
   * NOT removed — they hold their position in the stepper. Used by the GPC
   * workflow to always show its full form-stage list while greying the stages
   * that do not apply to the current buy. Independent of the standalone gate; a
   * step is non-navigable if it is disabled by EITHER mechanism. Unlike the
   * standalone gate, `isViewOnly` does NOT override these — an inapplicable
   * stage stays greyed even in a read-only example. Defaults to none.
   */
  disabledStageIndices?: number[];
  /** Tooltip shown when hovering a `disabledStageIndices` step. */
  disabledStageTooltip?: string;
  /**
   * Per-stage tooltip reasons (stepper index → reason text). When a disabled
   * step's index has an entry here, that specific reason is shown instead of
   * the single `disabledStageTooltip` fallback. Non-breaking: when omitted (or
   * missing the index) the logic falls back to `disabledStageTooltip`.
   */
  disabledStageReasons?: Record<number, string>;
  /**
   * Steps omitted from the visible navigation when the server has supplied an
   * authoritative applicability envelope. Their canonical index remains in
   * place so persisted workflow navigation never shifts.
   */
  hiddenStageIndices?: number[];
  /** Presentation-only destinations retain a flag cue in the arrow flow. */
  unnumberedStageIndices?: number[];
  /**
   * Presentation-only stage indices with an authorized, unreviewed edit by a
   * different collaborator. The caller must omit unavailable stages; this
   * shared wrapper also suppresses the cue for hidden or disabled steps.
   */
  updatedStageIndices?: number[];
  /** Safe server-projected component updates for the currently rendered MR stage. */
  marketResearchInlineEdits?: MarketResearchInlineEdit[];
  /** Backend stage code paired with `marketResearchInlineEdits`. */
  marketResearchBackendStageCode?: number;
  /**
   * Identity used to key the stage-body enter/exit transition. Changing it
   * triggers a subtle fade + slide swap of the stage content (the chrome —
   * stepper/header/nav — stays put). Optional and back-compatible: when
   * omitted it defaults to the active stage index (`stage-{currentStage}`),
   * so every stage routed through StageWrapper animates with no caller change.
   */
  transitionKey?: string;
  /**
   * Workflow type for the trailing per-stage rating widget. When set, a thin
   * right-aligned footer holding ONLY the thumbs-up/down rating control renders
   * below the stage content. When omitted, no rating footer renders
   * (back-compat for stages not yet wired).
   */
  workflowType?: WorkflowType;
  /** Saved per-stage votes for the current user, keyed by `stage_id`. */
  stageRatings?: Record<string, RatingValue>;
  /** Notified when the rating widget records a new vote for a `stage_id`. */
  onStageRatingChange?: (stageId: string, value: RatingValue | null) => void;
  /** Fired on thumbs-down so the host can open the feedback dialog. */
  onStageThumbsDown?: () => void;
}

const StageWrapper: React.FC<StageWrapperProps> = ({
  config,
  currentStage,
  stages,
  workflowName,
  workflowId,
  workflowTypeName,
  onBack,
  onStageSelect,
  children,
  fullWidth = false,
  compactNavigation = false,
  isProcessing = false,
  processingMessage = 'Processing...',
  standaloneMode = false,
  standaloneStartIndex = 11,
  isViewOnly = false,
  readOnlyReason,
  isSampleFlow = false,
  onSampleContinue,
  numItemsDetected = 1,
  disabledStageIndices,
  disabledStageTooltip = 'Not applicable for this purchase',
  disabledStageReasons,
  hiddenStageIndices,
  unnumberedStageIndices,
  updatedStageIndices,
  marketResearchInlineEdits,
  marketResearchBackendStageCode,
  transitionKey,
  workflowType,
  stageRatings,
  onStageRatingChange,
  onStageThumbsDown,
}) => {
  const stageTransitionKey = transitionKey ?? `stage-${currentStage}`;
  const activeStageRef = React.useRef<HTMLButtonElement | null>(null);
  const navigationScrollRef = React.useRef<HTMLDivElement | null>(null);
  const stageItemRefs = React.useRef<Array<HTMLLIElement | null>>([]);
  const intrinsicStageWidthsRef = React.useRef<Record<number, number>>({});
  const fillOneRowRef = React.useRef(false);

  const disabledStageSet = React.useMemo(
    () => new Set(disabledStageIndices ?? []),
    [disabledStageIndices],
  );
  const hiddenStageSet = React.useMemo(
    () => new Set(hiddenStageIndices ?? []),
    [hiddenStageIndices],
  );
  const visibleStageIndices = stages.map((_, index) => index)
    .filter((index) => !hiddenStageSet.has(index));
  const layoutKey = visibleStageIndices.map((index) => `${index}:${stages[index]}`).join('|');
  const [twoRowLayout, setTwoRowLayout] = React.useState<
    { breakIndex: number; width: number } | null | undefined
  >(undefined);
  const [fillOneRow, setFillOneRow] = React.useState(false);
  const [navigationWidth, setNavigationWidth] = React.useState(0);
  const previousNavigationWidthRef = React.useRef(0);

  React.useLayoutEffect(() => {
    let cancelled = false;
    const updateLayout = () => {
      const scroller = navigationScrollRef.current;
      if (!scroller) return;
      const width = scroller.clientWidth;
      setNavigationWidth(width);
      // The scroller has 4px on each side; reserve the list's trailing space
      // so the final arrow's focus outline remains visible at max scroll.
      const available = width - 8 - STAGE_NAV_END_CLEARANCE;
      const widths = visibleStageIndices.map((index) => {
        const item = stageItemRefs.current[index];
        const measured = item?.getBoundingClientRect().width ?? 0;
        if (!fillOneRowRef.current) {
          intrinsicStageWidthsRef.current[index] = measured;
          return measured;
        }
        // A filled row stretches each LI, so its rendered width is no longer
        // the width used to decide whether the intrinsic arrows fit. The label
        // keeps its natural width inside the expanded button.
        const button = item?.querySelector<HTMLElement>('[data-stage-navigation-button]');
        const label = button?.querySelector<HTMLElement>('[data-stage-label]');
        const labelWidth = label?.getBoundingClientRect().width ?? 0;
        if (button && labelWidth > 0) {
          const style = window.getComputedStyle(button);
          const horizontalPadding = (parseFloat(style.paddingLeft) || 0)
            + (parseFloat(style.paddingRight) || 0);
          const flag = button.querySelector<HTMLElement>('[data-testid="unnumbered-step-icon"]');
          const flagWidth = flag ? flag.getBoundingClientRect().width
            + (parseFloat(style.columnGap || style.gap) || 0) : 0;
          // Editor stages impose a larger CSS minimum than the base hit target.
          const computedMinWidth = parseFloat(style.minWidth);
          const minimumWidth = Number.isFinite(computedMinWidth) ? computedMinWidth : 44;
          const intrinsic = Math.max(44, minimumWidth, labelWidth + horizontalPadding + flagWidth);
          intrinsicStageWidthsRef.current[index] = intrinsic;
          return intrinsic;
        }
        return intrinsicStageWidthsRef.current[index] ?? measured;
      });
      if (available <= 0 || widths.some((width) => width <= 0)) {
        fillOneRowRef.current = false;
        setFillOneRow(false);
        setTwoRowLayout(null);
        return;
      }
      const gap = 0;
      const total = widths.reduce((sum, width) => sum + width, 0) + (widths.length - 1) * gap;
      if (total <= available) {
        fillOneRowRef.current = true;
        setFillOneRow(true);
        setTwoRowLayout(null);
        return;
      }
      fillOneRowRef.current = false;
      setFillOneRow(false);
      if (compactNavigation || visibleStageIndices.length <= 8) {
        setTwoRowLayout(null);
        return;
      }
      const candidates = Array.from({ length: widths.length - 1 }, (_, index) => index + 1)
        .map((breakIndex) => {
          const firstRow = widths.slice(0, breakIndex);
          const secondRow = widths.slice(breakIndex);
          return {
            breakIndex,
            firstRowWidth: firstRow.reduce((sum, width) => sum + width, 0) + (firstRow.length - 1) * gap,
            secondRowWidth: secondRow.reduce((sum, width) => sum + width, 0) + (secondRow.length - 1) * gap,
          };
        })
        .filter((candidate) => candidate.firstRowWidth <= available && candidate.secondRowWidth <= available)
        // The first row takes the longest ordered prefix that fits. Neither
        // row stretches short cells to match a cell in the other row.
        .sort((a, b) => b.breakIndex - a.breakIndex);
      const best = candidates[0];
      setTwoRowLayout((previous) => {
        if (!best) return null;
        if (previous?.breakIndex === best.breakIndex
          && Math.abs(previous.width - available) < 1) {
          return previous;
        }
        return { breakIndex: best.breakIndex, width: available };
      });
    };

    updateLayout();
    window.addEventListener('resize', updateLayout);
    const observer = typeof ResizeObserver === 'undefined'
      ? undefined : new ResizeObserver(updateLayout);
    if (navigationScrollRef.current) observer?.observe(navigationScrollRef.current);
    visibleStageIndices.forEach((index) => {
      if (stageItemRefs.current[index]) observer?.observe(stageItemRefs.current[index]!);
    });
    document.fonts?.ready.then(() => {
      if (!cancelled) updateLayout();
    });
    return () => {
      cancelled = true;
      window.removeEventListener('resize', updateLayout);
      observer?.disconnect();
    };
  // The primitive key avoids remeasuring when callers recreate an equal stage array.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutKey, compactNavigation]);

  React.useEffect(() => {
    const widthChanged = previousNavigationWidthRef.current !== navigationWidth;
    previousNavigationWidthRef.current = navigationWidth;
    if (twoRowLayout === null && !fillOneRow) {
      const active = activeStageRef.current;
      const scroller = navigationScrollRef.current;
      if (!active) return;
      if (widthChanged && scroller) {
        const buttonBounds = active.getBoundingClientRect();
        const scrollportLeft = scroller.getBoundingClientRect().left + scroller.clientLeft;
        // Keep the existing 4px focus-outline space without disturbing a
        // manually positioned row whose current destination still fits.
        if (buttonBounds.left >= scrollportLeft + 4
          && buttonBounds.right <= scrollportLeft + navigationWidth - 4) return;
      }
      active.scrollIntoView?.({ block: 'nearest', inline: 'center' });
    }
  }, [currentStage, twoRowLayout, fillOneRow, layoutKey, navigationWidth]);
  const unnumberedStageSet = React.useMemo(
    () => new Set(unnumberedStageIndices ?? []),
    [unnumberedStageIndices],
  );
  const updatedStageSet = React.useMemo(
    () => new Set(updatedStageIndices ?? []),
    [updatedStageIndices],
  );
  const assistantHeader = useContext(WorkflowAssistantHeaderContext);
  const assistantHeaderSlotRef = React.useRef<HTMLDivElement | null>(null);
  React.useLayoutEffect(() => {
    const slot = assistantHeaderSlotRef.current;
    return assistantHeader && slot ? assistantHeader.registerSlot(slot) : undefined;
  }, [assistantHeader]);
  const activeStageId = workflowType ? stageRatingId(workflowType, currentStage) : '';
  const ratingStageLabel =
    [workflowTypeName, config?.name].filter(Boolean).join(' › ') || (workflowName ?? '');
  const arrowInset = 4;
  return (
    <Container
      maxWidth={fullWidth ? false : 'xl'}
      sx={(theme) => ({ maxWidth: '100%', minWidth: 0, px: 2, py: 1, ...documentEditorWrapperSx, [phoneMedia(theme)]: { px: MOBILE_DENSITY.pageInset, py: 1 } })}
      data-testid="stage-wrapper"
      data-layout-width={fullWidth ? 'full' : 'contained'}
    >
      {/* Read-only example banner — shown for curated training/example flows
          (backend `metadata.read_only`). Defense-in-depth + UX: the backend
          also blocks writes, but this tells the user the flow is view-only. */}
      {isViewOnly && (
        <Alert
          severity="info"
          icon={<VisibilityIcon fontSize="inherit" />}
          sx={{ mb: 2 }}
          data-testid={readOnlyReason === 'training' || isSampleFlow ? 'read-only-example-banner' : 'read-only-workflow-banner'}
        >
          {readOnlyReason === 'similar_requirements'
            ? 'View only — Similar Requirements. You can view saved content and download available exports. Contact the owner for editing access.'
            : readOnlyReason === 'super_admin_view'
              ? 'View only — Super Admin inspection. You can view saved content and download available exports. Editing requires ownership or explicit shared edit access.'
            : readOnlyReason === 'training' || isSampleFlow
              ? 'Read-only example — this is a curated training workflow. You can page through every stage to see its inputs and outputs, but it cannot be modified.'
              : 'View only — this workflow cannot be modified.'}
        </Alert>
      )}

      {/* Progress Stepper */}
      <Paper
        variant="outlined"
        data-stage-navigation-panel
        sx={{ mb: 0, minWidth: 0, overflow: 'hidden', p: 0.5, ...(assistantHeader ? { display: 'flex', alignItems: 'center', gap: 1 } : {}) }}
      >
        <Box
          ref={navigationScrollRef}
          data-testid="stage-navigation-scroll"
          sx={{
            maxWidth: '100%',
            ...(assistantHeader ? { flex: '1 1 0%', minWidth: 0 } : {}),
            overflowX: 'auto',
            overflowY: 'hidden',
            px: 0.5,
            py: 0.5,
            width: '100%',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <Box component="nav" aria-label="Workflow stages">
            <Box
              component="ol"
              data-testid="stage-navigation"
              data-stage-count={stages.length}
              data-stage-navigation-layout={twoRowLayout ? 'two-row' : fillOneRow ? 'inline-fill' : 'inline-scroll'}
              data-stage-row-break={twoRowLayout?.breakIndex}
              sx={{
                display: 'flex',
                flexWrap: twoRowLayout ? 'wrap' : 'nowrap',
                gap: '0px',
                listStyle: 'none',
                m: 0,
                p: 0,
                pr: twoRowLayout ? 0 : `${STAGE_NAV_END_CLEARANCE}px`,
                width: twoRowLayout ? `${twoRowLayout.width}px` : fillOneRow ? '100%' : 'max-content',
              }}
            >
              {stages.map((label, index) => {
                const labelLines = stageLabelLines(label);
                const visiblePosition = visibleStageIndices.indexOf(index);
                const isFirstVisible = visiblePosition === 0;
                const isLastVisible = visiblePosition >= 0
                  && visiblePosition === visibleStageIndices.length - 1;
                const isDisabledByStandalone =
                  !isViewOnly && standaloneMode && index < standaloneStartIndex;
                const isDisabledByApplicability = disabledStageSet.has(index);
                const isDisabled = isDisabledByStandalone || isDisabledByApplicability;
                const isHidden = hiddenStageSet.has(index);
                const isUnnumbered = unnumberedStageSet.has(index);
                const isUpdated = updatedStageSet.has(index)
                  && !isDisabled
                  && !isHidden
                  && !isUnnumbered;
                const state = index === currentStage
                  ? 'current'
                  : index < currentStage ? 'earlier' : 'upcoming';
                const statusLabel = state === 'current'
                  ? 'Current' : state === 'earlier' ? 'Earlier' : 'Upcoming';
                const disabledReason = isDisabledByStandalone
                  ? 'Not available in standalone mode'
                  : isDisabledByApplicability
                    ? (disabledStageReasons?.[index] ?? disabledStageTooltip)
                    : '';
                const updatedLabel = `${label} — updated by another collaborator`;
                const accessibleLabel = `${isDisabled ? 'Unavailable' : statusLabel} stage: ${label}`
                  + (isUpdated ? ' — updated by another collaborator' : '')
                  + (disabledReason ? ` — ${disabledReason}` : '');

                return (
                  <Box
                    component="li"
                    key={`${index}-${label}`}
                    ref={(element) => { stageItemRefs.current[index] = element as HTMLLIElement | null; }}
                    sx={{
                      display: isHidden ? 'none' : 'block',
                      flex: fillOneRow ? '1 0 auto' : '0 0 auto',
                      listStyle: 'none',
                      position: 'relative',
                      '&:focus-within': {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: 2,
                        zIndex: 1,
                      },
                    }}
                  >
                    <Tooltip
                      title={disabledReason ? `${label} — ${disabledReason}` : isUpdated ? updatedLabel : label}
                      placement="bottom"
                      slotProps={{ tooltip: { sx: { zIndex: 2100 } } }}
                    >
                      <Box component="span" sx={{ display: 'block', position: 'relative' }}>
                        <Box
                          component="button"
                          type="button"
                          ref={index === currentStage ? activeStageRef : undefined}
                          data-stage-navigation-button
                          data-stage-state={state}
                          aria-current={index === currentStage ? 'step' : undefined}
                          aria-label={accessibleLabel}
                          disabled={isDisabled || !onStageSelect}
                          onClick={() => onStageSelect?.(index)}
                          sx={(theme) => ({
                            alignItems: 'center',
                            backgroundColor: isDisabled
                              ? theme.palette.action.disabledBackground
                              : state === 'current'
                                ? theme.palette.primary.main
                                : state === 'earlier'
                                  ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.28 : 0.16)
                                  : theme.palette.action.hover,
                            border: 0,
                            clipPath: isLastVisible
                              ? isFirstVisible
                                ? 'polygon(0 0, 100% 0, 100% 100%, 0 100%)'
                                : `polygon(0 0, 100% 0, 100% 100%, 0 100%, ${arrowInset}px 50%)`
                              : isFirstVisible
                                ? `polygon(0 0, calc(100% - ${arrowInset}px) 0, 100% 50%, calc(100% - ${arrowInset}px) 100%, 0 100%)`
                                : `polygon(0 0, calc(100% - ${arrowInset}px) 0, 100% 50%, calc(100% - ${arrowInset}px) 100%, 0 100%, ${arrowInset}px 50%)`,
                            color: isDisabled
                              ? theme.palette.text.disabled
                              : state === 'current'
                                ? theme.palette.primary.contrastText
                                : theme.palette.text.primary,
                            cursor: isDisabled ? 'not-allowed' : onStageSelect ? 'pointer' : 'default',
                            display: 'inline-flex',
                            fontFamily: 'inherit',
                            fontSize: 13,
                            fontWeight: state === 'current' ? 700 : 600,
                            gap: 0.5,
                            justifyContent: 'center',
                            lineHeight: 1.2,
                            maxWidth: 'none',
                            minHeight: '44px',
                            minWidth: 44,
                            width: fillOneRow ? '100%' : 'max-content',
                            px: 0.5,
                            py: 0.5,
                            textAlign: 'center',
                            whiteSpace: 'normal',
                            '&:hover:not(:disabled)': {
                              filter: 'brightness(0.94)',
                            },
                          })}
                        >
                          {isUnnumbered && (
                            <OutlinedFlagIcon aria-hidden="true" data-testid="unnumbered-step-icon" sx={{ fontSize: 16, flexShrink: 0 }} />
                          )}
                          <Box
                            component="span"
                            data-stage-label
                            sx={{
                              display: 'inline-flex',
                              flexDirection: 'column',
                              minWidth: 0,
                            }}
                          >
                            {labelLines.map((line, lineIndex) => (
                              <Box
                                component="span"
                                data-stage-label-line
                                key={lineIndex}
                                sx={{
                                  display: 'block',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {line}{lineIndex < labelLines.length - 1 ? ' ' : ''}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                        {isUpdated && (
                          <Box
                            aria-hidden="true"
                            data-testid={`stage-updated-dot-${index}`}
                            data-edit-awareness-tone="red"
                            sx={{
                              backgroundColor: (theme) => theme.palette.marketResearch.editAwareness,
                              border: '2px solid',
                              borderColor: 'background.paper',
                              borderRadius: '50%',
                              height: 9,
                              position: 'absolute',
                              right: 8,
                              top: -3,
                              width: 9,
                            }}
                          />
                        )}
                      </Box>
                    </Tooltip>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
        {assistantHeader && <Box ref={assistantHeaderSlotRef} data-workflow-assistant-header-slot sx={{ flex: '0 0 auto', display: 'flex' }} />}
      </Paper>

      {/* Multi-item warning banner — visible from Stage 1 onward whenever
          the backend's num_items_detected count is greater than 1. Rendered
          here in the layout wrapper so every stage inherits the banner
          without per-stage code changes. */}
      <MultiItemWarningBanner numItemsDetected={numItemsDetected} />

      {/* Processing Indicator */}
      {isProcessing && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <CircularProgress size={20} />
            <Typography variant="body2">{processingMessage}</Typography>
          </Box>
        </Alert>
      )}

      {/* Main Content */}
      <MarketResearchEditAwarenessProvider
        stageCode={marketResearchBackendStageCode ?? null}
        changes={marketResearchInlineEdits ?? []}
      >
      <FlowContext.Provider
        value={{ flowName: workflowName ?? '', workflowType, onBack, canGoBack: currentStage > 0, numItemsDetected, standaloneMode: standaloneMode ?? false, isViewOnly, readOnlyReason, isSampleFlow, onSampleContinue }}
      >
        <Paper
          variant="outlined"
          data-testid="stage-content-paper"
          sx={(theme) => ({
            p: workflowType === 'market_research' ? 2 : 3,
            pt: 0.5,
            mb: 2,
            minHeight: workflowType === 'market_research' ? 0 : '400px',
            ...(workflowType === 'market_research' && {
              '&:has([data-document-editor-stage]), &:has(.section-document-editor)': {
                p: 3,
                pt: 0.5,
                minHeight: '400px',
                [phoneMedia(theme)]: { p: MOBILE_DENSITY.cardInset, pt: 0.5, minHeight: 0 },
              },
            }),
            [phoneMedia(theme)]: { p: MOBILE_DENSITY.cardInset, pt: 0.5, minHeight: 0 },
          })}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', minHeight: 0 }}>
            <MarketResearchEditIndicatorSlot componentKey="stage" targetLabel={config.name} />
          </Box>
          {/* Only the stage body animates on stage/page change; the stepper,
              header, banner and processing chrome above stay mounted so they
              do not flicker. */}
          <Transition transitionKey={stageTransitionKey}>{children}</Transition>
        </Paper>
      </FlowContext.Provider>
      </MarketResearchEditAwarenessProvider>

      {/* Restored trailing footer slot (cf. removed bottom-nav panel,
          f239d2dcb). It holds ONLY the per-stage rating widget, right-aligned,
          and renders below the content Paper — so it sits beneath whatever
          Back/Continue/Export row each stage drew. No Back, no generic
          Continue: those stay per-stage by design. */}
      {workflowType && !isViewOnly && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <StageRatingWidget
            workflowType={workflowType}
            uiStageIndex={currentStage}
            stageLabel={ratingStageLabel}
            flowId={workflowId}
            currentValue={stageRatings?.[activeStageId] ?? null}
            onThumbsDown={onStageThumbsDown ?? (() => {})}
            onChange={(v) => onStageRatingChange?.(activeStageId, v)}
          />
        </Box>
      )}
    </Container>
  );
};

export default StageWrapper;
